import { EmbeddedDocument } from 'features/chatbot/hooks/useEmbeddedDocuments'
import React, { createContext, useCallback, useContext, useState } from 'react'

export interface ChatbotAttachmentContextType {
  attachments: EmbeddedDocument[]
  addAttachment: (doc: EmbeddedDocument) => void
  removeAttachment: (id: string) => void
  clearAttachments: () => void
}

export const ChatbotAttachmentContext = createContext<
  ChatbotAttachmentContextType | undefined
>(undefined)

export const ChatbotAttachmentProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [attachments, setAttachments] = useState<EmbeddedDocument[]>([])

  const addAttachment = useCallback((doc: EmbeddedDocument) => {
    setAttachments((prev) => {
      if (prev.some((a) => a.id === doc.id)) {
        return prev
      }
      return [doc, ...prev]
    })
  }, [])

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }, [])

  const clearAttachments = useCallback(() => {
    setAttachments([])
  }, [])

  return (
    <ChatbotAttachmentContext.Provider
      value={{
        attachments,
        addAttachment,
        removeAttachment,
        clearAttachments,
      }}>
      {children}
    </ChatbotAttachmentContext.Provider>
  )
}

export const useChatbotAttachmentsOptional = ():
  | ChatbotAttachmentContextType
  | undefined => {
  return useContext(ChatbotAttachmentContext)
}

export const useChatbotAttachments = (): ChatbotAttachmentContextType => {
  const context = useContext(ChatbotAttachmentContext)
  if (context === undefined) {
    throw new Error(
      'useChatbotAttachmentsRequired must be used within a ChatbotAttachmentProvider'
    )
  }
  return context
}
